import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetPublicProgramByIdQuery,
  useGetPublicProgramBySlugQuery,
} from "../../app/features/program/programApi";
import SEOData from "../../components/SEO/SEO";
import { useAppSelector } from "../../app/hooks";
import { useCurrentToken } from "../../app/features/auth/authSlice";

const SITE_ORIGIN = import.meta.env.VITE_SITE_ORIGIN || "https://uniedu.fi";

// Pure-numeric param → legacy id URL; anything else → slug
const isNumericId = (v) => /^\d+$/.test(String(v || ""));

function DetailRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <span className="material-symbols-outlined text-[20px] text-[#8B5CF6] flex-shrink-0 mt-0.5">
        {icon}
      </span>
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">
          {label}
        </p>
        <p className="text-sm text-slate-700 font-medium">{value}</p>
      </div>
    </div>
  );
}

function LanguageSection({ program }) {
  const tests = [
    { label: "IELTS", value: program.ielts_overall },
    { label: "PTE", value: program.pte_overall },
    { label: "TOEFL", value: program.toefl_overall },
    { label: "Duolingo", value: program.duolingo_overall },
  ].filter((t) => t.value);

  if (tests.length === 0)
    return <p className="text-sm text-slate-600">No language requirement</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {tests.map((t) => (
        <span
          key={t.label}
          className="px-3 py-1 bg-purple-100 text-[#4C1D95] text-xs font-bold rounded-full"
        >
          {t.label} {t.value}+
        </span>
      ))}
    </div>
  );
}

export default function ProgramDetail() {
  const { id: rawParam } = useParams();
  const navigate = useNavigate();
  const token = useAppSelector(useCurrentToken);
  const isLoggedIn = !!token;
  const useId = isNumericId(rawParam);

  const idQuery = useGetPublicProgramByIdQuery(rawParam, { skip: !useId });
  const slugQuery = useGetPublicProgramBySlugQuery(rawParam, { skip: useId });
  const { data: program, isLoading, isError } = useId ? idQuery : slugQuery;

  // Legacy /programs/{id} → redirect to canonical /programs/{slug}
  useEffect(() => {
    if (!program?.slug) return;
    if (rawParam !== program.slug) {
      navigate(`/programs/${program.slug}`, { replace: true });
    }
  }, [program, rawParam, navigate]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 animate-pulse space-y-6">
        <div className="h-8 bg-purple-200 rounded w-2/3" />
        <div className="h-4 bg-purple-100 rounded w-1/3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !program) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <span className="material-symbols-outlined text-6xl text-slate-300">
          error
        </span>
        <p className="text-xl font-bold text-slate-600 mt-4">Program not found</p>
        <button
          onClick={() => navigate("/programs")}
          className="mt-6 px-6 py-2 bg-[#8B5CF6] text-white rounded-lg font-semibold hover:bg-[#6D28D9] transition-colors"
        >
          Back to Programs
        </button>
      </div>
    );
  }

  const deadline = program.application_end
    ? new Date(program.application_end).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Open intake";

  const canonicalUrl = `${SITE_ORIGIN}/programs/${program.slug}`;
  const seoTitle = `${program.program_type_display} in ${program.subject_name} | UniEdu`;
  const plainDescription = (program.description || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const tuitionText = program.tuition_fee
    ? `Tuition €${Number(program.tuition_fee).toLocaleString()} / year. `
    : "";
  const seoDescription = (
    plainDescription ||
    `${program.program_type_display} in ${program.subject_name} in ${program.city}, ${program.country}. ${tuitionText}Apply through UniEdu.`
  ).slice(0, 160);

  const courseLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: `${program.program_type_display} in ${program.subject_name}`,
    description: seoDescription,
    url: canonicalUrl,
    provider: {
      "@type": "CollegeOrUniversity",
      address: {
        "@type": "PostalAddress",
        addressLocality: program.city,
        addressCountry: program.country,
      },
    },
    ...(program.tuition_fee && {
      offers: {
        "@type": "Offer",
        price: program.tuition_fee,
        priceCurrency: "EUR",
        category: "Tuition",
      },
    }),
  };

  return (
    <div className="bg-[#F9FAFB] min-h-screen font-poppins">
      <SEOData
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        image={program.image || `${SITE_ORIGIN}/og-default.png`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(courseLd)}</script>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back */}
        <button
          onClick={() => navigate("/programs")}
          className="flex items-center gap-1 text-sm text-[#8B5CF6] font-semibold mb-8 hover:text-[#6D28D9] transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back to Programs
        </button>

        {/* Header card */}
        <div
          className="rounded-2xl p-8 mb-8 text-white"
          style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #4C1D95 100%)" }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-purple-200 text-sm font-semibold uppercase tracking-widest mb-2">
                {program.program_type_display}
              </p>
              <h1 className="font-montserrat text-3xl font-bold leading-tight">
                {program.program_type_display} in {program.subject_name}
              </h1>
              <p className="text-purple-200 mt-2 text-lg">
                {program.city}, {program.country}
              </p>
            </div>
            {program.scholarship && (
              <span className="px-3 py-1.5 bg-green-400 text-white text-xs font-bold rounded-full uppercase tracking-wider self-start">
                Scholarship Available
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Details card */}
          <div className="md:col-span-2 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <h2 className="font-montserrat font-bold text-lg text-[#4C1D95] mb-4">
              Program Details
            </h2>
            <DetailRow icon="schedule" label="Duration" value={program.duration ? `${program.duration} Years • Full-time` : null} />
            <DetailRow icon="event_note" label="Application Deadline" value={deadline} />
            <DetailRow icon="payments" label="Tuition Fee" value={program.tuition_fee ? `€${program.tuition_fee.toLocaleString()} per academic year` : "Contact for fees"} />
            <DetailRow icon="payments" label="Application Fee" value={program.application_fee ? `€${program.application_fee.toLocaleString()}` : null} />
            <DetailRow icon="location_on" label="Location" value={`${program.city}, ${program.country}`} />
            <DetailRow icon="school" label="Scholarship" value={program.scholarship || null} />

            {/* Language */}
            <div className="py-3">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-[#8B5CF6]">translate</span>
                Language Requirements
              </p>
              <LanguageSection program={program} />
            </div>

          </div>

          {/* Apply card */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col">
            <h2 className="font-montserrat font-bold text-lg text-[#4C1D95] mb-4">
              Ready to Apply?
            </h2>
            <p className="text-sm text-slate-500 mb-6 flex-grow">
              Submit your application for{" "}
              <span className="font-semibold text-slate-700">
                {program.program_type_display} in {program.subject_name}
              </span>{" "}
              in {program.city}, {program.country}.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => isLoggedIn ? navigate("/student/academic-profile") : navigate("/sign-in")}
                className="w-full py-3 text-white font-bold rounded-xl shadow-lg transition-all"
                style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)" }}
              >
                Apply Now
              </button>
              <button
                onClick={() => navigate("/contact")}
                className="w-full py-3 text-[#8B5CF6] font-bold rounded-xl border border-[#8B5CF6] hover:bg-purple-50 transition-all"
              >
                Ask a Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
